import { useState, useEffect, useCallback } from 'preact/hooks';
import QRCode from 'qrcode';
import { dataHub } from '../lib/datahub';

// Per-user login 2FA management (authenticator-app TOTP). Enrollment: get a secret + otpauth
// URI from the edge, render the QR LOCALLY (the secret never goes to a third party), confirm
// with a code. Disable requires a current code. The secret is shown once for manual entry too.

type Msg = { text: string; ok: boolean } | null;
const CODE_RE = /^\d{6}$/;

export function TwoFactorPanel({ onClose }: { onClose: () => void }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);   // null = loading
  const [secret, setSecret] = useState('');                       // pending enrollment secret (shown once)
  const [qrSvg, setQrSvg] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);

  const loadStatus = useCallback(async () => {
    try {
      const s = await dataHub.authStatus();
      setEnabled(Boolean(s.twoFactor && s.twoFactor.enabled));
    } catch (e) {
      setMsg({ text: (e as Error).message, ok: false });
      setEnabled(false);
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const beginSetup = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await dataHub.twoFactorSetup();
      setSecret(r.secret);
      setQrSvg(await QRCode.toString(r.otpauthUri, { type: 'svg', margin: 1, errorCorrectionLevel: 'M', color: { dark: '#e8e6df', light: '#00000000' } }));
    } catch (e) {
      setMsg({ text: (e as Error).message, ok: false });
    } finally { setBusy(false); }
  };

  const confirmEnable = async (e: Event) => {
    e.preventDefault();
    setMsg(null);
    if (!CODE_RE.test(code)) return setMsg({ text: 'Enter the 6-digit code from your app.', ok: false });
    setBusy(true);
    try {
      await dataHub.twoFactorEnable(code);
      setSecret(''); setQrSvg(''); setCode('');
      setEnabled(true);
      setMsg({ text: '2FA is on. You’ll enter a code from your app at every sign-in.', ok: true });
    } catch (e) {
      setMsg({ text: (e as Error).message, ok: false });
    } finally { setBusy(false); }
  };

  const disable = async (e: Event) => {
    e.preventDefault();
    setMsg(null);
    if (!CODE_RE.test(code)) return setMsg({ text: 'Enter a current 6-digit code to turn 2FA off.', ok: false });
    setBusy(true);
    try {
      await dataHub.twoFactorDisable(code);
      setCode('');
      setEnabled(false);
      setMsg({ text: '2FA is off.', ok: true });
    } catch (e) {
      setMsg({ text: (e as Error).message, ok: false });
    } finally { setBusy(false); }
  };

  return (
    <div class="om-auth-gate" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div class="om-auth-card om-2fa-card">
        <p class="om-auth-kicker">Oculus-Clipeus - Security</p>
        <h2>Two-factor authentication</h2>

        {enabled === null ? (
          <p class="om-auth-splash">Loading…</p>
        ) : enabled ? (
          <form onSubmit={disable} autocomplete="off">
            <p class="om-good" style="margin:0 0 8px">✓ 2FA is on — sign-in requires a code from your authenticator app.</p>
            <p class="om-auth-hint" style="margin:0 0 8px">To turn it off, enter a current 6-digit code.</p>
            <label for="om2faOff">Current code</label>
            <input id="om2faOff" type="text" inputMode="numeric" maxLength={6} autocomplete="one-time-code" value={code}
              onInput={(e) => setCode((e.target as HTMLInputElement).value.replace(/\D/g, ''))} />
            {msg && <p class={msg.ok ? 'om-good' : 'om-auth-error'} style="margin:8px 0 0">{msg.text}</p>}
            <div class="om-admin-form-actions" style="margin-top:10px">
              <button class="om-auth-error-btn" type="submit" disabled={busy}>{busy ? 'Disabling…' : 'Disable 2FA'}</button>
            </div>
          </form>
        ) : !secret ? (
          <>
            <p class="om-auth-hint" style="margin:0 0 10px">
              Add a second factor with any authenticator app (Google Authenticator, Authy, 1Password…). Free, and your
              codes are generated on your device — nothing is sent by SMS or email.
            </p>
            {msg && <p class={msg.ok ? 'om-good' : 'om-auth-error'} style="margin:0 0 8px">{msg.text}</p>}
            <div class="om-admin-form-actions">
              <button class="om-auth-primary" type="button" onClick={beginSetup} disabled={busy}>{busy ? 'Starting…' : 'Enable 2FA'}</button>
            </div>
          </>
        ) : (
          <form onSubmit={confirmEnable} autocomplete="off">
            <p class="om-auth-hint" style="margin:0 0 10px">Scan this with your authenticator app, then enter the 6-digit code it shows to confirm.</p>
            {qrSvg && <div class="om-2fa-qr" dangerouslySetInnerHTML={{ __html: qrSvg }} />}
            <p class="om-auth-kicker" style="margin:8px 0 2px">Can’t scan? Enter this key manually:</p>
            <code class="om-2fa-secret">{secret.replace(/(.{4})/g, '$1 ').trim()}</code>
            <label for="om2faOn" style="margin-top:8px;display:block">6-digit code</label>
            <input id="om2faOn" type="text" inputMode="numeric" maxLength={6} autocomplete="one-time-code" value={code}
              onInput={(e) => setCode((e.target as HTMLInputElement).value.replace(/\D/g, ''))} />
            {msg && <p class={msg.ok ? 'om-good' : 'om-auth-error'} style="margin:8px 0 0">{msg.text}</p>}
            <div class="om-admin-form-actions" style="margin-top:10px">
              <button class="om-auth-primary" type="submit" disabled={busy}>{busy ? 'Confirming…' : 'Confirm + turn on'}</button>
            </div>
          </form>
        )}

        <div class="om-admin-form-actions" style="margin-top:14px">
          <button class="om-auth-secondary" type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
